import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ArrowRight,
  Layers,
  Coins,
  ClipboardCheck,
  Target,
  Route,
  GraduationCap,
  Users,
  Scale,
  LucideIcon,
} from "lucide-react";
import { PageContainer } from "@/components/layouts/MainLayout";
import { T } from "@/components/editable/T";
import { getLocalizedRoutePath } from "@/lib/routeUtils";

type RouteKey = Parameters<typeof getLocalizedRoutePath>[0];

export interface ModuleCardDef {
  routeKey: RouteKey;
  titleKey: string;
  titleDefault: string;
  bodyKey: string;
  bodyDefault: string;
  ctaKey?: string;
  ctaDefault?: string;
}

interface ModuleCardsProps {
  /** Translation page namespace of the page */
  translationPage: string;
  /** Section title */
  titleKey: string;
  titleDefault: string;
  /** Optional intro line below the heading */
  introDefault?: string;
  /** Translation key for the intro line (defaults to "componentsIntro") */
  introKey?: string;
  cards: ModuleCardDef[];
  /** Optional line rendered centered below the cards */
  footer?: React.ReactNode;
  /** Optional background color override for the section */
  backgroundColor?: string;
}

const routeIcons: Partial<Record<string, LucideIcon>> = {
  jobProfiles: Layers,
  salaryScales: Coins,
  reviews: ClipboardCheck,
  goalsTeamPerformance: Target,
  careerPaths: Route,
  developmentGoals: GraduationCap,
  talentReview: Users,
  compensationRound: Coins,
  payTransparencyEqualPay: Scale,
  performanceManagement: ClipboardCheck,
  compensation: Scale,
  workDesign: Layers,
};

export const ModuleCards = ({ translationPage, titleKey, titleDefault, introDefault, introKey = "componentsIntro", cards, footer, backgroundColor = "#F3EDFD" }: ModuleCardsProps) => {
  const { i18n } = useTranslation();
  const lang = i18n.language || "nl";
  const gridCols = cards.length <= 1 ? "" : cards.length === 2 ? "md:grid-cols-2" : "md:grid-cols-2 lg:grid-cols-3";


  return (
    <section className="px-6 py-20 mt-20" style={{ backgroundColor }}>
      <PageContainer>
        <div className="text-left">
          <h2 className="feature-section-heading text-foreground">
            <T page={translationPage} k={titleKey} d={titleDefault} />
          </h2>
          {introDefault && (
            <p
              className="text-left"
              style={{ color: "#46315D", maxWidth: 640, marginTop: 8, marginBottom: 40 }}
            >
              <T page={translationPage} k={introKey} d={introDefault} />
            </p>
          )}
        </div>
        <div className={`grid gap-6 grid-cols-1 ${gridCols}`}>
          {cards.map((card) => {
            const Icon = routeIcons[card.routeKey] ?? Layers;
            return (
              <Link
                key={card.routeKey}
                to={getLocalizedRoutePath(card.routeKey, lang)}
                className="group flex flex-col text-left bg-white rounded-2xl overflow-hidden border border-border hover:shadow-lg hover:-translate-y-1 transition-all duration-200 cursor-pointer w-full h-full"
              >
                <div className="p-7 flex flex-col flex-1">
                  <div className="flex items-center gap-4">
                    <span
                      className="inline-flex items-center justify-center w-12 h-12 shrink-0 rounded-xl"
                      style={{ backgroundColor: "#F3EDFD" }}
                    >
                      <Icon className="w-5 h-5" style={{ color: "#9055EE" }} />
                    </span>
                    <h3 className="text-lg font-semibold text-foreground group-hover:text-[#9055EE] transition-colors">
                      <T page={translationPage} k={card.titleKey} d={card.titleDefault} />
                    </h3>
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed max-w-[44ch] mt-3">
                    <T page={translationPage} k={card.bodyKey} d={card.bodyDefault} />
                  </p>
                  <span className="inline-flex items-center gap-1 self-start text-sm font-medium text-[#9055EE] mt-auto pt-4 group-hover:gap-2 transition-all">
                    <T page={translationPage} k="modules.ctaShort" d="Ontdek" />
                    <ArrowRight className="w-3.5 h-3.5" />
                  </span>
                </div>
              </Link>
            );
          })}
        </div>
        {footer && (
          <p className="mt-10 text-center text-sm" style={{ color: "#46315D" }}>
            {footer}
          </p>
        )}
      </PageContainer>
    </section>
  );
};
